import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTopicDetails, deleteTopic, getTopicDigest, markTopicArticlesRead } from '../services/topics';
import { createDigestHighlight, getDigestHighlightsByTopic } from '../services/highlights';
import { useToast } from '../hooks/useToast';
import { useTextSelection } from '../hooks/useTextSelection';
import { setTitle } from '../utils/environment';
import { stripMarkdownFormatting } from '../utils/markdown';
import Pagination from '../components/Pagination';
import ShareContextMenu from '../components/ShareContextMenu';
import SharePreviewDialog from '../components/SharePreviewDialog';
import useNavigation from '../hooks/useNavigation';

// Simple markdown renderer for digest text (handles **bold** and citations)
const renderMarkdown = (text) => {
  if (!text) return text;

  // Helper to process bold text within a segment
  const processBold = (str, keyPrefix) => {
    const parts = str.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`${keyPrefix}-b-${idx}`} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Split by citation pattern (来源: ...) to style separately
  // Match both ASCII () and Chinese full-width （） parentheses
  const citationRegex = /([（(]来源[:：]\s*[^)）]+[)）])/g;
  const segments = text.split(citationRegex);

  return segments.map((segment, idx) => {
    if (segment.match(/^[（(]来源[:：]\s*[^)）]+[)）]$/)) {
      // Citation - render as italic and grey
      return (
        <span key={idx} className="italic text-gray-400 text-sm ml-1">
          {segment}
        </span>
      );
    }
    // Regular text - process for bold
    return <span key={idx}>{processBold(segment, idx)}</span>;
  });
};

const TopicDetail = () => {
  const { topicId } = useParams();
  const { navigateToArticle, goBack } = useNavigation();
  const { showSuccess, showError } = useToast();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(() => {
    const savedToggle = sessionStorage.getItem(`topic-${topicId}-showAll`);
    return savedToggle === 'true';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestExpanded, setDigestExpanded] = useState(true);
  const [markingRead, setMarkingRead] = useState(false);
  const [digestHighlights, setDigestHighlights] = useState([]);
  const [highlightingItem, setHighlightingItem] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [capturedSelection, setCapturedSelection] = useState({
    text: '',
    markdown: '',
    hasMarkdown: false
  });
  const [isCreatingHighlight, setIsCreatingHighlight] = useState(false);

  // Refs for digest section detection
  const whatsNewRef = useRef(null);
  const coreThemeRef = useRef(null);
  const recommendedReadsRef = useRef(null);

  // Text selection hook
  const {
    selectedText,
    selectedMarkdown,
    hasMarkdown,
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    hideSelection,
  } = useTextSelection();

  const fetchTopicDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch articles with pagination
      const topicData = await getTopicDetails(topicId, {
        unread_only: !showAll,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      });
      setTopic(topicData);
      setPagination(topicData.pagination);
      setTitle(`${topicData.label} - Topics`);
    } catch (err) {
      console.error('Failed to fetch topic details:', err);
      setError(err.message);
      setTitle('Topic Not Found');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (!window.confirm(`Are you sure you want to delete the topic "${topic?.label}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      await deleteTopic(topicId);
      showSuccess('Topic deleted successfully');
      goBack(); // Go back to previous page
    } catch (err) {
      console.error('Failed to delete topic:', err);
      showError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleArticleClick = (articleId) => {
    navigateToArticle(articleId, {
      context: {
        from: 'topic',
        topicId: topicId,
        topicLabel: topic?.label
      },
      pageState: {
        showAll: showAll,
        currentPage: currentPage,
        pageSize: pageSize
      },
      preserveHistory: true
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (perPage) => {
    setPageSize(perPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const fetchDigest = async () => {
    if (!topicId) return;

    try {
      setDigestLoading(true);
      const digestData = await getTopicDigest(topicId);
      setDigest(digestData);
      setDigestExpanded(true);
      // Fetch existing digest highlights for this topic
      if (topic) {
        await fetchDigestHighlights();
      }
    } catch (err) {
      console.error('Failed to fetch digest:', err);
      showError(err.message);
    } finally {
      setDigestLoading(false);
    }
  };

  const fetchDigestHighlights = async () => {
    if (!topic) return;

    try {
      const response = await getDigestHighlightsByTopic(topic.label);
      setDigestHighlights(response.highlights || []);
    } catch (err) {
      console.error('Failed to fetch digest highlights:', err);
      // Don't show error to user, just log it
    }
  };

  const handleHighlightDigestItem = async (digestText, section) => {
    if (!digest || !topic) return;

    const itemKey = `${section}-${digestText}`;
    setHighlightingItem(itemKey);

    try {
      const highlightData = {
        article_id: null, // General digest highlight (not article-associated)
        topic_name: topic.label,
        digest_section: section,
        digest_text: digestText,
        digest_version: digest.generated_at,
      };

      await createDigestHighlight(highlightData);
      showSuccess('Digest item highlighted');

      // Refresh highlights
      await fetchDigestHighlights();
    } catch (err) {
      console.error('Failed to highlight digest item:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setHighlightingItem(null);
    }
  };

  const isDigestItemHighlighted = (digestText) => {
    return digestHighlights.some(h => h.exact_text === digestText);
  };

  // Detect which digest section the selection is in
  const detectDigestSection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;

    // Check each section ref
    if (whatsNewRef.current?.contains(element)) return 'whats_new';
    if (coreThemeRef.current?.contains(element)) return 'core_theme';
    if (recommendedReadsRef.current?.contains(element)) return 'recommended_reads';

    return null;
  };

  // Handle highlight from context menu (text selection)
  const handleCreateHighlightFromSelection = async () => {
    if (!selectedText || !digest || !topic) {
      hideSelection();
      return;
    }

    const section = detectDigestSection();
    if (!section) {
      showError('Please select text from the digest content');
      hideSelection();
      return;
    }

    try {
      setIsCreatingHighlight(true);

      const highlightData = {
        article_id: null,
        topic_name: topic.label,
        digest_section: section,
        digest_text: selectedText,
        digest_version: digest.generated_at,
      };

      await createDigestHighlight(highlightData);
      showSuccess('Text highlighted');
      await fetchDigestHighlights();
      hideSelection();
    } catch (err) {
      console.error('Failed to create highlight:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setIsCreatingHighlight(false);
    }
  };

  // Share functionality handlers
  const handleShareClick = () => {
    setCapturedSelection({
      text: selectedText,
      markdown: selectedMarkdown,
      hasMarkdown: hasMarkdown
    });
    setShareDialogOpen(true);
    hideSelection();
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setCapturedSelection({
      text: '',
      markdown: '',
      hasMarkdown: false
    });
  };

  const handleMarkAllRead = async () => {
    if (!topicId) return;

    try {
      setMarkingRead(true);
      const result = await markTopicArticlesRead(topicId);
      showSuccess(result.message || `Marked ${result.marked_count} articles as read`);

      // Refresh topic details to update unread counts
      await fetchTopicDetails();
    } catch (err) {
      console.error('Failed to mark articles as read:', err);
      showError(err.message);
    } finally {
      setMarkingRead(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatSimilarity = (similarity) => {
    return Math.round(similarity * 100);
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.9) return 'text-green-600 bg-green-50';
    if (similarity >= 0.8) return 'text-green-600 bg-green-50';
    if (similarity >= 0.7) return 'text-blue-600 bg-blue-50';
    if (similarity >= 0.6) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  useEffect(() => {
    if (topicId) {
      fetchTopicDetails();
    }
  }, [topicId, currentPage, pageSize, showAll]);

  // Handle refresh when returning from article navigation
  useEffect(() => {
    if (location.state?.refreshData) {
      fetchTopicDetails();
    }
  }, [location.state?.refreshData]);

  // Automatically fetch digest when topic loads (if there are unread articles)
  useEffect(() => {
    if (topic && topic.unread_count > 0 && !digest && !digestLoading) {
      fetchDigest();
    }
  }, [topic?.id, topic?.unread_count]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-600 border-t-transparent"></div>
            <span className="ml-4 text-lg text-gray-600">Loading topic details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !topic) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Topic Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'The topic you\'re looking for doesn\'t exist.'}</p>
            <Link
              to="/topics"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              ← Back to Topics
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // No need for client-side filtering since backend handles it with unread_only parameter
  const filteredArticles = topic.articles;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-gray-700">Dashboard</Link>
            <span className="mx-2">→</span>
            <Link to="/topics" className="hover:text-gray-700">Topics</Link>
            <span className="mx-2">→</span>
            <span className="text-gray-800">{topic.label}</span>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  {topic.label}
                </h1>
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <span>📄 {topic.unread_count} unread</span>
                  <span>📚 {topic.article_count} total articles</span>
                  <span>🗓️ Created {formatDate(topic.created_at)}</span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleDeleteTopic}
                  disabled={deleting}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleting ? 'Deleting...' : '🗑️ Delete Topic'}
                </button>
              </div>
            </div>

            {/* Topic Metadata */}
            {topic.topic_metadata && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Algorithm:</span>
                    <div className="text-gray-600">{topic.algorithm}</div>
                  </div>
                  {topic.topic_metadata.silhouette_score && (
                    <div>
                      <span className="font-medium text-gray-700">Quality Score:</span>
                      <div className="text-gray-600">
                        {(topic.topic_metadata.silhouette_score * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-gray-700">Cluster Size:</span>
                    <div className="text-gray-600">{topic.topic_metadata.article_count}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Generation:</span>
                    <div className="text-gray-600 capitalize">
                      {topic.topic_metadata.generated_from || 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Digest */}
        {topic.unread_count > 0 && (
          <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
            {!digest && !digestExpanded ? (
              <button
                onClick={fetchDigest}
                disabled={digestLoading}
                className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">🤖</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">AI Digest</h3>
                      <p className="text-sm text-gray-600">Get a summary of unread articles in this topic</p>
                    </div>
                  </div>
                  {digestLoading ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
                  ) : (
                    <span className="text-gray-400">▶</span>
                  )}
                </div>
              </button>
            ) : digest && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">🤖</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">AI Digest</h3>
                      <p className="text-xs text-gray-500">
                        {digest.article_count} articles analyzed • {new Date(digest.generated_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleMarkAllRead}
                      disabled={markingRead || topic.unread_count === 0}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {markingRead ? 'Marking...' : `✓ Mark All Read`}
                    </button>
                    <button
                      onClick={() => setDigestExpanded(!digestExpanded)}
                      className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      {digestExpanded ? '▼' : '▶'}
                    </button>
                  </div>
                </div>

                {digestExpanded && (
                  <div className="space-y-6">
                    {/* What's New */}
                    <div ref={whatsNewRef}>
                      <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="text-lg mr-2">📰</span>
                        What&apos;s New
                      </h4>
                      <ul className="space-y-3">
                        {digest.whats_new.map((item, idx) => {
                          const isHighlighted = isDigestItemHighlighted(item);
                          const itemKey = `whats_new-${item}`;
                          const isHighlighting = highlightingItem === itemKey;

                          return (
                            <li
                              key={idx}
                              className={`group relative text-base text-gray-700 leading-relaxed pl-4 border-l-2 ${
                                isHighlighted ? 'border-yellow-400 bg-yellow-50 py-2 pr-2 rounded-r' : 'border-blue-300'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">{renderMarkdown(item)}</div>
                                <button
                                  onClick={() => handleHighlightDigestItem(item, 'whats_new')}
                                  disabled={isHighlighted || isHighlighting}
                                  className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                    isHighlighted
                                      ? 'text-yellow-700 bg-yellow-100 cursor-default'
                                      : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 opacity-0 group-hover:opacity-100'
                                  }`}
                                  title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                                >
                                  {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    {/* Core Theme */}
                    <div ref={coreThemeRef}>
                      <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="text-lg mr-2">🎯</span>
                        Core Theme
                      </h4>
                      <ul className="space-y-3">
                        {digest.core_theme.map((item, idx) => {
                          const isHighlighted = isDigestItemHighlighted(item);
                          const itemKey = `core_theme-${item}`;
                          const isHighlighting = highlightingItem === itemKey;

                          return (
                            <li
                              key={idx}
                              className={`group relative text-base text-gray-700 leading-relaxed pl-4 border-l-2 ${
                                isHighlighted ? 'border-yellow-400 bg-yellow-50 py-2 pr-2 rounded-r' : 'border-purple-300'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">{renderMarkdown(item)}</div>
                                <button
                                  onClick={() => handleHighlightDigestItem(item, 'core_theme')}
                                  disabled={isHighlighted || isHighlighting}
                                  className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                    isHighlighted
                                      ? 'text-yellow-700 bg-yellow-100 cursor-default'
                                      : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 opacity-0 group-hover:opacity-100'
                                  }`}
                                  title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                                >
                                  {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    {/* Recommended Reads */}
                    {digest.recommended_reads && digest.recommended_reads.length > 0 && (
                      <div ref={recommendedReadsRef}>
                        <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                          <span className="text-lg mr-2">⭐</span>
                          Recommended Reads
                        </h4>
                        <div className="space-y-3">
                          {digest.recommended_reads.map((read, idx) => {
                            const isHighlighted = isDigestItemHighlighted(read.reason);
                            const itemKey = `recommended_reads-${read.reason}`;
                            const isHighlighting = highlightingItem === itemKey;

                            return (
                              <div
                                key={idx}
                                className={`group p-4 rounded-lg border transition-colors cursor-pointer ${
                                  isHighlighted
                                    ? 'bg-yellow-50 border-yellow-300 hover:border-yellow-400'
                                    : 'bg-amber-50 border-amber-200 hover:border-amber-300'
                                }`}
                                onClick={() => handleArticleClick(read.article_id)}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="font-medium text-gray-800 text-base flex-1">{read.title}</div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleHighlightDigestItem(read.reason, 'recommended_reads');
                                    }}
                                    disabled={isHighlighted || isHighlighting}
                                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                      isHighlighted
                                        ? 'text-yellow-700 bg-yellow-100 cursor-default'
                                        : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 opacity-0 group-hover:opacity-100'
                                    }`}
                                    title={isHighlighted ? 'Already highlighted' : 'Highlight this reason'}
                                  >
                                    {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                  </button>
                                </div>
                                <div className="text-base text-gray-600 italic">{renderMarkdown(read.reason)}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Saved Highlights Section */}
                    {digestHighlights.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                          <span className="text-lg mr-2">💾</span>
                          Saved Highlights
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({digestHighlights.length})
                          </span>
                        </h4>
                        <div className="space-y-2">
                          {digestHighlights.map((highlight) => (
                            <div
                              key={highlight.id}
                              className="bg-yellow-50 border-l-4 border-yellow-400 px-4 py-3 rounded-r-md"
                            >
                              <p className="text-gray-900 text-sm leading-relaxed">
                                {highlight.exact_text}
                              </p>
                              <div className="mt-2 flex items-center text-xs text-gray-500">
                                {highlight.digest_section && (
                                  <span className="mr-2">
                                    {highlight.digest_section === 'whats_new' && '📰 What\'s New'}
                                    {highlight.digest_section === 'core_theme' && '🎯 Core Theme'}
                                    {highlight.digest_section === 'recommended_reads' && '⭐ Recommended Reads'}
                                  </span>
                                )}
                                <span>•</span>
                                <span className="ml-2">
                                  {new Date(highlight.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filter Controls */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setShowAll(checked);
                    sessionStorage.setItem(`topic-${topicId}-showAll`, checked.toString());
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Show all</span>
              </label>
            </div>
            <div className="text-sm text-gray-500">
              {pagination ?
                `Showing ${Math.min((pagination.page - 1) * pagination.per_page + 1, pagination.total)} - ${Math.min(pagination.page * pagination.per_page, pagination.total)} of ${pagination.total} articles` :
                `${filteredArticles.length} articles`
              }
            </div>
          </div>
        </div>

        {/* Articles List */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Articles in Topic</h2>
            <p className="text-gray-600 text-sm mt-1">
              Articles are ordered by similarity to the topic cluster center
            </p>
          </div>

          {filteredArticles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-4">📰</div>
              <p className="text-lg font-medium">No articles found</p>
              <p className="text-sm mt-1">
                {showAll
                  ? 'This topic has no articles'
                  : 'No unread articles in this topic'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredArticles.map((article) => (
                <div
                  key={article.id}
                  onClick={() => handleArticleClick(article.id)}
                  className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-medium text-gray-800 leading-6 mr-4">
                          {article.title}
                        </h3>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSimilarityColor(article.similarity)}`}>
                            {formatSimilarity(article.similarity)}% match
                          </span>
                          {!article.is_read && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Unread
                            </span>
                          )}
                        </div>
                      </div>

                      {article.summary && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {stripMarkdownFormatting(article.summary)}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          {article.author && (
                            <span>👤 {article.author}</span>
                          )}
                          {article.domain && (
                            <span>🌐 {article.domain}</span>
                          )}
                        </div>
                        <span>📅 {formatDate(article.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            totalItems={pagination.total}
            itemsPerPage={pagination.per_page}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            isLoading={loading}
            className="mt-8"
          />
        )}
      </div>

      {/* Context Menu for text selection in digest */}
      {selectedText && digest && (
        <ShareContextMenu
          visible={contextMenuVisible}
          position={contextMenuPosition}
          onShare={handleShareClick}
          onHighlight={handleCreateHighlightFromSelection}
          onClose={hideSelection}
          hasMarkdown={hasMarkdown}
          showHighlight={true}
          isHighlighting={isCreatingHighlight}
          ref={contextMenuRef}
        />
      )}

      {/* Share Preview Dialog */}
      <SharePreviewDialog
        isOpen={shareDialogOpen}
        onClose={handleCloseShareDialog}
        selectedText={capturedSelection.text}
        selectedMarkdown={capturedSelection.markdown}
        hasMarkdown={capturedSelection.hasMarkdown}
        articleTitle={topic?.label}
        articleUrl={`/topics/${topicId}`}
      />
    </div>
  );
};

export default TopicDetail;